import React, { useState, useEffect } from "react";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import { BASE_URL } from "./Constants";
import "./Upload.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const NewReports = () => {

  const [question, setQuestion] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedDept, setSelectedDept] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");

  const [deptOptions, setDeptOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [detailsData, setDetailsData] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedEmpId, setSelectedEmpId] = useState("");

  /* ================= FETCH API ================= */
  useEffect(() => {
    const fetchReports = async () => {
      try {
        const token = localStorage.getItem("authToken");

        const response = await fetch(`${BASE_URL}/new_reports`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch reports");
        }

        const data = await response.json();

        // ✅ Safe data handling
        const safeData = Array.isArray(data?.data) ? data.data : [];

        // ✅ Set main data once
        setQuestion(safeData);

        // ✅ Unique department dropdown
        const uniqueDepts = [
          ...new Set(safeData.map(item => item.dept_name).filter(Boolean))
        ];

        // ✅ Unique location dropdown
        const uniqueLocations = [
          ...new Set(safeData.map(item => item.location_name).filter(Boolean))
        ];

        setDeptOptions(uniqueDepts);
        setLocationOptions(uniqueLocations);

        setLoading(false);

      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  /* ================= FILTER LOGIC ================= */
  useEffect(() => {
    let filtered = question;

    // 🔎 Search by EMP ID
    if (searchQuery) {
      filtered = filtered.filter((item) =>
        item.emp_id?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // 📅 Year filter
    if (selectedYear) {
      filtered = filtered.filter(
        (item) => item.year === selectedYear
      );
    }

    // 🏢 Department filter
    if (selectedDept) {
      filtered = filtered.filter(
        (item) => item.dept_name === selectedDept
      );
    }

    // 📍 Location filter
    if (selectedLocation) {
      filtered = filtered.filter(
        (item) => item.location_name === selectedLocation
      );
    }

    setFilteredQuestions(filtered);
    setCurrentPage(1);

  }, [question, searchQuery, selectedYear, selectedDept, selectedLocation]);

  /* ================= PAGINATION ================= */
  const paginateQuestions = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredQuestions.slice(
      startIndex,
      startIndex + itemsPerPage
    );
  };

  const handleViewDetails = async (empId) => {
    try {
      setSelectedEmpId(empId);
      setShowModal(true);
      setDetailsLoading(true);

      const response = await fetch(
        `${BASE_URL}/get_details/${empId}`
      );

      const result = await response.json();

      if (result.status) {
        setDetailsData(result.data);
      }

      setDetailsLoading(false);
    } catch (error) {
      console.error(error);
      setDetailsLoading(false);
    }
  };

  const totalMarks = detailsData.reduce(
    (sum, item) => sum + Number(item.secured_marks || 0),
    0
  );
  /* ================= UI ================= */

  const downloadExcel = () => {

    if (!filteredQuestions.length) {
      alert("No data to export");
      return;
    }

    // ✅ Format data for Excel
    const excelData = filteredQuestions.map((item, index) => ({
      "S.No": index + 1,
      "Emp ID": item.emp_id,
      "Employee Name": item.comp_name,
      "Viva Marks": item.viva_total_marks,
      "Overall Remarks": item.overall_remarks,
      "Training Needs": item.training_needs,
      "Interviewer Feedback": item.interviewer_feed_back,
      "Feedback Marks": item.feedback_marks,
      "Department": item.dept_name,
      "Location": item.location_name,
      "Year": item.year,
      "Created On": item.created_on,
    }));

    // ✅ Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // ✅ Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reports");

    // ✅ Generate file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const fileData = new Blob([excelBuffer], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(fileData, `Employee_Reports_${new Date().getFullYear()}.xlsx`);
  };

  return (
    <div className="container-scroller">
      <Navbar />

      <div className="container-fluid page-body-wrapper">
        <Sidebar />

        <div className="main-panel">
          <div className="content-wrapper">

            <div className="card">
              <div className="card-body">

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="card-title mb-0">Employee Reports</h4>

                  <button
                    className="btn btn-success"
                    onClick={downloadExcel}
                  >
                    ⬇ Download Excel
                  </button>
                </div>
                {/* FILTER ROW */}
                <div className="row mb-3">

                  {/* Rows */}
                  <div className="col-lg-1">
                    <select
                      className="form-control"
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  {/* SEARCH */}
                  <div className="col-lg-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by Employee ID"
                      value={searchQuery}
                      onChange={(e) =>
                        setSearchQuery(e.target.value)
                      }
                    />
                  </div>

                  {/* YEAR */}
                  <div className="col-lg-2">
                    <select
                      className="form-control"
                      value={selectedYear}
                      onChange={(e) =>
                        setSelectedYear(e.target.value)
                      }
                    >
                      <option value="">All Years</option>
                      <option value="2026">2026</option>
                      <option value="2025">2025</option>
                    </select>
                  </div>

                  {/* DEPARTMENT */}
                  <div className="col-lg-3">
                    <select
                      className="form-control"
                      value={selectedDept}
                      onChange={(e) =>
                        setSelectedDept(e.target.value)
                      }
                    >
                      <option value="">All Departments</option>
                      {deptOptions.map((d, i) => (
                        <option key={i}>{d}</option>
                      ))}
                    </select>
                  </div>

                  {/* LOCATION */}
                  <div className="col-lg-3">
                    <select
                      className="form-control"
                      value={selectedLocation}
                      onChange={(e) =>
                        setSelectedLocation(e.target.value)
                      }
                    >
                      <option value="">All Locations</option>
                      {locationOptions.map((l, i) => (
                        <option key={i}>{l}</option>
                      ))}
                    </select>
                  </div>



                </div>

                {/* TABLE */}
                {loading && <p>Loading...</p>}
                {error && <p style={{ color: "red" }}>{error}</p>}

                {!loading && filteredQuestions.length > 0 && (
                  <>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Emp ID</th>
                          <th>Emp Name</th>
                          <th>Dept Name</th>
                          <th>Viva Marks</th>
                          <th>Overall Remarks</th>
                          <th>Training Needs</th>
                          <th>Interviewer Feedback</th>
                          <th>Feedback Marks</th>
                          <th>Year</th>
                          <th>Created On</th>
                          <th>Check Question Vise Marks</th>
                        </tr>
                      </thead>

                      <tbody>
                        {paginateQuestions().map((item, index) => (
                          <tr key={index}>
                            <td>
                              {(currentPage - 1) * itemsPerPage +
                                index +
                                1}
                            </td>
                            <td>{item.emp_id}</td>
                            <td>{item.comp_name}</td>
                            <td>{item.dept_name}</td>
                            <td>{item.viva_total_marks}</td>
                            <td style={{
                              wordWrap: "break-word",
                              whiteSpace: "normal",
                              maxWidth: "200px"
                            }}>
                              {item.overall_remarks}
                            </td>
                            <td style={{
                              wordWrap: "break-word",
                              whiteSpace: "normal",
                              maxWidth: "200px"
                            }}>{item.training_needs}</td>
                            <td>{item.interviewer_feed_back}</td>
                            <td>{item.feedback_marks}</td>
                            <td>{item.year}</td>
                            <td>{item.created_on}</td>
                            <td>
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => handleViewDetails(item.emp_id)}
                              >
                                👁
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* PAGINATION */}
                    <div className="pagination mt-3">
                      <button
                        className="btn btn-secondary"
                        disabled={currentPage === 1}
                        onClick={() =>
                          setCurrentPage((p) => p - 1)
                        }
                      >
                        Previous
                      </button>

                      <span className="mx-3">
                        Page {currentPage}
                      </span>

                      <button
                        className="btn btn-secondary"
                        disabled={
                          currentPage ===
                          Math.ceil(
                            filteredQuestions.length /
                            itemsPerPage
                          )
                        }
                        onClick={() =>
                          setCurrentPage((p) => p + 1)
                        }
                      >
                        Next
                      </button>
                    </div>
                  </>
                )}

                {!loading && filteredQuestions.length === 0 && (
                  <p>No records found</p>
                )}

              </div>
            </div>
            {showModal && (
              <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.5)" }}>
                <div className="modal-dialog modal-lg">
                  <div className="modal-content">

                    <div className="modal-header">
                      <h5 className="modal-title">
                        Question Wise Marks — EMP ID : {selectedEmpId}
                      </h5>

                      <button
                        className="btn-close"
                        onClick={() => setShowModal(false)}
                      ></button>
                    </div>

                    <div className="modal-body">

                      {detailsLoading ? (
                        <p>Loading...</p>
                      ) : (
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Question Type</th>
                              <th>Question</th>
                              <th>Marks</th>
                              <th>Remarks</th>
                            </tr>
                          </thead>

                          <tbody>
                            {detailsData.map((d, i) => (
                              <tr key={i}>
                                <td>{i + 1}</td>
                                <td>{d.question_type_name}</td>
                                <td style={{
                              wordWrap: "break-word",
                              whiteSpace: "normal",
                              maxWidth: "200px"
                            }}>{d.question}</td>
                                <td>{d.secured_marks}</td>
                                <td>{d.remarks}</td>
                              </tr>
                            ))}
                            <tr style={{ fontWeight: "bold", background: "#f5f5f5" }}>
                              <td colSpan="3" style={{ textAlign: "right" }}>
                                Total Marks
                              </td>
                              <td>{totalMarks}</td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      )}

                    </div>

                  </div>
                </div>
              </div>
            )}

          </div>

          <footer className="footer">
            © {new Date().getFullYear()} Vijaya Diagnostic Centre
          </footer>
        </div>
      </div>
    </div>
  );
};

export default NewReports;
